/**
 * Centralised constants and helpers.
 * Anything that would otherwise be a magic string lives here so it can be
 * tweaked in one place and surfaced as user-editable later if desired.
 */
import { deriveDefaults } from "./booking-fields";

// IANA timezone shortlist — Australia first, then common offshore.
// Schema default for sites is 'Australia/Melbourne'.
export const TIMEZONES = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Perth",
  "Australia/Hobart",
  "Australia/Darwin",
  "Pacific/Auckland",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Hong_Kong",
  "Europe/London",
  "America/Los_Angeles",
  "America/New_York",
  "UTC",
];

export const AU_STATES = [
  { value: "VIC", label: "Victoria" },
  { value: "NSW", label: "New South Wales" },
  { value: "QLD", label: "Queensland" },
  { value: "SA", label: "South Australia" },
  { value: "WA", label: "Western Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NT", label: "Northern Territory" },
];

// Slot-duration options shown in the picker. Schema default = 30.
export const SLOT_DURATION_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 20, label: "20 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "60 min" },
  { value: 90, label: "90 min" },
  { value: 120, label: "120 min" },
];

// 12 recommended bay-color swatches (Tailwind palette). Three greys are
// grouped at the end and are the preferred choice for inactive / archived bays.
// Bays accept any hex value; this is just the picker UI list.
export const BAY_COLOR_PALETTE = [
  { value: "#3b82f6", label: "Blue" },
  { value: "#10b981", label: "Emerald" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#ef4444", label: "Red" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#ec4899", label: "Pink" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#0ea5e9", label: "Sky" },
  { value: "#84cc16", label: "Lime" },
  // 3 greys (preferred for inactive bays)
  { value: "#94a3b8", label: "Slate (inactive)" },
  { value: "#9ca3af", label: "Gray (inactive)" },
  { value: "#a8a29e", label: "Stone (inactive)" },
];

// Bay max-booking-hours options. Schema default = 4.
export const MAX_BOOKING_HOURS_OPTIONS = [1, 2, 3, 4, 6, 8, 10, 12, 16, 24];

// Form defaults — single source of truth (mirrors schema DEFAULTs).
export const SITE_DEFAULTS = {
  name: "",
  code: "",
  city: "",
  state: "VIC",
  country: "Australia",
  postcode: "",
  address_line1: "",
  address_line2: "",
  timezone: "Australia/Melbourne",
  operating_hours_start: "07:00",
  operating_hours_end: "17:00",
  slot_duration_minutes: 30,
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  is_active: true,
  // Booking-policy defaults (site-wide; individual bays may override) ---
  min_booking_minutes: "",
  max_booking_hours: "",
  min_lead_time_hours: "",
  max_horizon_days: "",
  max_concurrent_bookings: "",
  requires_approval: false,
  allow_multiday: true,
  requires_check_in_out: false,
  // PR-1 buffer feature (migration 150) — minutes to leave empty after each
  // booking at this site. 0 = feature inactive even when the client toggle is on.
  default_buffer_minutes: 0,
};

export const BAY_DEFAULTS = {
  name: "",
  code: "",
  description: "",
  location_instructions: "",
  booking_note: "",
  max_vehicle_length: "",
  max_vehicle_height: "",
  max_vehicle_weight: "",
  max_booking_hours: 4,
  slot_duration_minutes: "", // "" = inherit from site
  requires_approval: true,
  allow_multiday: false,
  color: "#3b82f6",
  sort_order: 0,
  is_active: true,
  // Booking-policy overrides ("" / null = inherit from site) -----------
  min_booking_minutes: "",
  min_lead_time_hours: "",
  max_horizon_days: "",
  max_concurrent_bookings: "",
  requires_check_in_out: null,   // null = inherit; true/false = explicit override
  is_on_demand: false,           // hidden from booker self-service / public links
  // PR-1 buffer feature (migration 150) — per-bay override. "" = inherit from
  // site; 0 = explicitly disable even when the site/client have one set.
  buffer_minutes: "",
};

/** Inheritance choices for a tri-state boolean override (bay → site). */
export const INHERIT_BOOLEAN_OPTIONS = [
  { value: "inherit", label: "Inherit from site" },
  { value: "true",    label: "Yes" },
  { value: "false",   label: "No" },
];

/** Strip empty-string values so we don't write empty strings into nullable columns. */
export function nullEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = v === "" ? null : v;
  }
  return out;
}

/* ================  BOOKINGS  ================ */

export const BOOKING_DIRECTIONS = [
  { value: "inbound",  label: "Inbound (receiving)" },
  { value: "outbound", label: "Outbound (dispatch)" },
];

// Mirrors the booking_status enum in 001_complete_schema.sql.
// Order matters: it drives the status filter dropdown order.
export const BOOKING_STATUSES = [
  { value: "draft",            label: "Draft",            tone: "slate"   },
  { value: "pending_approval", label: "Pending approval", tone: "amber"   },
  { value: "approved",         label: "Approved",         tone: "blue"    },
  { value: "rejected",         label: "Rejected",         tone: "rose"    },
  { value: "checked_in",       label: "Checked in",       tone: "indigo"  },
  { value: "loading",          label: "Loading",          tone: "violet"  },
  { value: "unloading",        label: "Unloading",        tone: "fuchsia" },
  { value: "completed",        label: "Completed",        tone: "emerald" },
  { value: "cancelled",        label: "Cancelled",        tone: "slate"   },
  { value: "no_show",          label: "No-show",          tone: "rose"    },
];

export const STATUS_BADGE_CLASS = {
  slate:   "bg-slate-100 text-slate-700 hover:bg-slate-100",
  amber:   "bg-amber-100 text-amber-800 hover:bg-amber-100",
  blue:    "bg-blue-100 text-blue-700 hover:bg-blue-100",
  rose:    "bg-rose-100 text-rose-700 hover:bg-rose-100",
  indigo:  "bg-indigo-100 text-indigo-700 hover:bg-indigo-100",
  violet:  "bg-violet-100 text-violet-700 hover:bg-violet-100",
  fuchsia: "bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100",
  emerald: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
};

/** Badge classes for a booking status value (status → tone → classes). */
export const statusBadgeClass = (status) =>
  STATUS_BADGE_CLASS[BOOKING_STATUSES.find((s) => s.value === status)?.tone || "slate"];

/** Mandatory pre-fill fields the booker MUST supply. If any is blank,
 *  the booking is "incomplete" and appears in the incomplete-bookings
 *  workflow on /bookings (filter chip + dashboard tile).
 *
 *  `rego_prime_mover` lives inside the `vehicle_regos` JSONB column
 *  (see api.js buildBookingPayload) so it needs a special check below. */
export const INCOMPLETE_BOOKING_FIELDS = [
  { key: "direction",        label: "Direction" },
  { key: "sender_company",   label: "Sender company" },
  { key: "receiver_company", label: "Receiver company" },
  { key: "vehicle_type",     label: "Vehicle type" },
  { key: "pallet_type",      label: "Pallet brand" },
];

/** Returns the list of missing-field labels for a booking row.
 *  A booking is "incomplete" iff this list is non-empty. */
export function bookingMissingFields(b) {
  if (!b) return [];
  const missing = [];
  for (const f of INCOMPLETE_BOOKING_FIELDS) {
    const v = b[f.key];
    if (v === null || v === undefined || v === "") missing.push(f.label);
  }
  // Prime-mover rego lives inside vehicle_regos JSONB
  const primeMover = b.vehicle_regos?.prime_mover;
  if (!primeMover) missing.push("Prime mover rego");
  // pallets: at least one direction must be > 0
  if ((Number(b.pallets_in)  || 0) === 0 &&
      (Number(b.pallets_out) || 0) === 0) {
    missing.push("Pallets in/out");
  }
  return missing;
}

export function isBookingIncomplete(b) {
  return bookingMissingFields(b).length > 0;
}

export const LOAD_RESTRAINT_METHODS = [
  { value: "curtain_sided", label: "Curtain-sided" },
  { value: "gates",         label: "Gates" },
  { value: "straps",        label: "Straps" },
  { value: "chains",        label: "Chains" },
  { value: "twist_locks",   label: "Twist locks (container)" },
  { value: "other",         label: "Other" },
];

/* =====================  RECURRING PATTERNS  =================== */
export const PATTERN_TYPES = [
  { value: "daily",       label: "Daily" },
  { value: "weekly",      label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly",     label: "Monthly (same date)" },
];

export const DAYS_OF_WEEK = [
  { value: 1, label: "Mon", long: "Monday" },
  { value: 2, label: "Tue", long: "Tuesday" },
  { value: 3, label: "Wed", long: "Wednesday" },
  { value: 4, label: "Thu", long: "Thursday" },
  { value: 5, label: "Fri", long: "Friday" },
  { value: 6, label: "Sat", long: "Saturday" },
  { value: 0, label: "Sun", long: "Sunday" },
];

export function patternDefaults() {
  const today = new Date();
  return {
    site_id: "",
    bay_id: "",
    pattern_type: "weekly",
    day_of_week: [1, 2, 3, 4, 5], // Mon–Fri
    time_start: "08:00",
    time_end: "09:00",
    start_date: today.toISOString().slice(0, 10),
    end_date: "",
    booking_template: {
      direction: "inbound",
      vehicle_type: "",
      sender_name: "",
      receiver_name: "",
      goods_description: "",
      notes: "",
    },
    requires_approval: true,
    is_active: true,
  };
}

/* =====================  BLACKOUTS  =================== */
export function blackoutDefaults() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    site_id: "",
    bay_id: "", // empty = site-wide
    title: "",
    reason: "",
    start_date: today,
    start_time: "00:00",
    end_date: today,
    end_time: "23:59",
    is_full_day: true,
  };
}

/**
 * Role-driven shading for blackout overlays on the dock grid.
 * Values track `lbs_blackouts.created_by_role` set by trigger 009.
 */
export const BLACKOUT_ROLE_STYLE = {
  super_admin:  { bg: "rgba(71, 85, 105, 0.55)",  ring: "rgba(71, 85, 105, 0.75)",  label: "Closed (super admin)" },     // slate
  client_admin: { bg: "rgba(71, 105, 145, 0.45)", ring: "rgba(71, 105, 145, 0.7)",  label: "Closed (client admin)" },    // blue-grey
  site_admin:   { bg: "rgba(146, 119, 64, 0.45)", ring: "rgba(146, 119, 64, 0.7)",  label: "Closed (site admin)" },      // amber-grey
  dock_manager: { bg: "rgba(146, 80, 80, 0.45)",  ring: "rgba(146, 80, 80, 0.7)",   label: "Closed (dock manager)" },    // red-grey
  default:      { bg: "rgba(148, 163, 184, 0.4)", ring: "rgba(100, 116, 139, 0.5)", label: "Closed" },
};

// Round `Date` up to the next multiple of N minutes.
function roundUpMinutes(d, step) {
  const ms = step * 60 * 1000;
  return new Date(Math.ceil(d.getTime() / ms) * ms);
}

/**
 * Status-workflow transitions: { fromStatus: [transitions...] }
 * Each transition: { to, label, tone, roles, requiresReason, directions }
 *   - roles      = role names that can perform it (omit for "anyone with edit access")
 *   - directions = restrict to specific booking direction(s)
 *   - requiresReason = pop a textarea before committing
 *   - destructive = render as red destructive button
 */
export const BOOKING_TRANSITIONS = {
  draft: [
    { to: "pending_approval", label: "Submit for approval", tone: "amber" },
    { to: "cancelled",        label: "Cancel booking",      tone: "slate", destructive: true },
  ],
  pending_approval: [
    { to: "approved",  label: "Approve", tone: "blue",
      roles: ["super_admin","client_admin","site_admin","dock_manager"] },
    { to: "rejected",  label: "Reject",  tone: "rose", requiresReason: true, destructive: true,
      roles: ["super_admin","client_admin","site_admin","dock_manager"] },
    { to: "cancelled", label: "Cancel",  tone: "slate", destructive: true },
  ],
  approved: [
    { to: "checked_in", label: "Check in", tone: "indigo" },
    { to: "no_show",    label: "Mark no-show", tone: "rose", destructive: true,
      roles: ["super_admin","client_admin","site_admin","dock_manager","gate"] },
    { to: "cancelled",  label: "Cancel", tone: "slate", destructive: true },
  ],
  checked_in: [
    { to: "loading",   label: "Start loading",   tone: "violet",  directions: ["outbound"] },
    { to: "unloading", label: "Start unloading", tone: "fuchsia", directions: ["inbound"] },
    { to: "cancelled", label: "Cancel",          tone: "slate", destructive: true,
      roles: ["super_admin","client_admin","site_admin","dock_manager"] },
  ],
  loading:   [
    { to: "completed", label: "Complete", tone: "emerald" },
    { to: "cancelled", label: "Cancel",   tone: "slate", destructive: true,
      roles: ["super_admin","client_admin","site_admin"] },
  ],
  unloading: [
    { to: "completed", label: "Complete", tone: "emerald" },
    { to: "cancelled", label: "Cancel",   tone: "slate", destructive: true,
      roles: ["super_admin","client_admin","site_admin"] },
  ],
  // Terminal states: no transitions
  completed: [],
  cancelled: [],
  rejected:  [],
  no_show:   [],
};

/** Initial values for the booking form. Defaults to "next half-hour, +60min".
 *
 *  Field defaults are declared in `/app/frontend/src/lib/booking-fields.js`
 *  (ROOT_FIELDS + JSONB group arrays). Only fields that CAN'T live in
 *  the static Map are inlined below:
 *    • booking_date / start_time / end_time — computed from now()
 *    • site_rules_accepted — form-only gate, never written to DB
 *
 *  Adding a new column to lbs_bookings? Add ONE entry to booking-fields.js
 *  and defaults/coercion/hydration all update automatically — this is
 *  what kills the driver_tba / broker_id class of drift bugs. */
export function bookingDefaults() {
  const now = roundUpMinutes(new Date(Date.now() + 5 * 60 * 1000), 30);
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  const fmtDate = (d) => d.toISOString().slice(0, 10);
  const fmtTime = (d) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

  return {
    ...deriveDefaults(),

    // Computed at call-time (can't live in the static Map)
    booking_date: fmtDate(now),
    start_time:   fmtTime(now),
    end_time:     fmtTime(end),

    // Form-only gate for the Confirm-and-save button; never sent to DB
    site_rules_accepted: false,
  };
}
